/* eslint-disable @typescript-eslint/no-use-before-define */
import { BottomAction } from '@euler/app/components/BottomAction';
import { LaunchScreenLikePageContainer } from '@euler/app/components/LaunchScreenLikePageContainer';
import {
  ReportScreenProps,
  useReportContext,
} from '@euler/app/flows/report/components/ReportContext';
import { ReportRef } from '@euler/app/flows/report/functions';
import { useReportHeaderMetrics } from '@euler/app/flows/report/functions/useReportHeaderMetrics';
import { TaskNavBar } from '@euler/app/flows/task/components/navbar/TaskNavBar';
import { kAppStoreSnapshotMode } from '@euler/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ReportTabKey } from '@euler/model/viewmodel';
import {
  FC,
  ReactElement,
  ReactNode,
  RefObject,
  forwardRef,
  memo,
  useCallback,
} from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  RefreshControl,
  ScrollViewProps,
  StyleProp,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { SharedValue, useSharedValue } from 'react-native-reanimated';
import { ReportHeader, ReportHeaderShade } from './ReportHeader';

export const ReportScrollView: FC<{
  title?: string;
  accessoryText?: string;
  contentContainerStyle?: StyleProp<ViewStyle>;
  offsetTop?: number;
  children?: ReactNode;
  tab?: ReportTabKey;
  action?: ReportScreenProps['action'];
  secondaryAction?: ReportScreenProps['secondaryAction'];
  target: ReportRef;
  transparentHeader?: boolean;
  scrollY?: SharedValue<number>;
  rightOptions?: () => ReactNode;
  onShare?: (tab?: ReportTabKey) => void;
  shareButtonRef?: RefObject<TouchableOpacity>;
  renderScrollView?: (
    props: ScrollViewProps,
    paddingTop: number,
  ) => ReactElement;
}> = memo(
  ({
    target,
    title,
    transparentHeader,
    accessoryText,
    contentContainerStyle,
    renderScrollView,
    offsetTop,
    action,
    secondaryAction,
    shareButtonRef,
    onShare,
    children,
    rightOptions,
    ...props
  }) => {
    const fallbackScrollY = useSharedValue(0);
    const scrollY = props.scrollY ?? fallbackScrollY;
    const { rpx } = useResponsiveContext();

    const { navBarHeight, headerHeight, safeAreaInsets, offset } =
      useReportHeaderMetrics();

    const onScroll = useCallback(
      (e: NativeSyntheticEvent<NativeScrollEvent>) => {
        'worklet';
        scrollY.value = e.nativeEvent.contentOffset.y;
      },
      [scrollY],
    );

    const paddingTop = transparentHeader
      ? 0
      : headerHeight + (offsetTop ?? -offset);

    const scrollViewProps: ScrollViewProps = {
      showsVerticalScrollIndicator: false,
      scrollEventThrottle: 1,
      onScroll,
      refreshControl:
        Platform.OS === 'android' || Platform.OS === 'web' ? undefined : (
          <Refresher progressViewOffset={navBarHeight} />
        ),
      children,
      contentContainerStyle: [
        {
          flexGrow: 1,
          paddingTop,
          paddingBottom: safeAreaInsets.bottom + rpx(64),
        },
        contentContainerStyle,
      ],
    };
    const licensePlateNo = kAppStoreSnapshotMode
      ? '沪AAAAAA'
      : target.licensePlateNo;

    return (
      <>
        <LaunchScreenLikePageContainer transparent={!transparentHeader}>
          {!transparentHeader && (
            <ReportHeaderShade scrollY={scrollY} headerHeight={headerHeight} />
          )}

          {renderScrollView != null ? (
            renderScrollView(scrollViewProps, paddingTop)
          ) : (
            <ScrollView {...scrollViewProps}>{children}</ScrollView>
          )}

          <ReportHeader
            licensePlateNo={licensePlateNo}
            vehicleName={target.vehicleName}
            vehicleImageUrl={target.vehicleImageUrl ?? undefined}
            scrollY={scrollY}
            headerHeight={headerHeight}
          />

          <TaskNavBar
            title={title ?? '报告详情'}
            accessoryText={accessoryText}
            licensePlateNo={licensePlateNo}
            vehicleName={target.vehicleName}
            scrollY={scrollY}
            rightOptions={rightOptions}
          />
        </LaunchScreenLikePageContainer>
        {(action != null || onShare != null || secondaryAction != null) && (
          <BottomAction scrollY={scrollY}>
            {secondaryAction != null && (
              <BottomAction.Button {...secondaryAction} />
            )}
            {action != null && <BottomAction.Button {...action} />}
            {onShare != null && (
              <BottomAction.Button
                text="分享报告"
                variant="primary"
                onPress={() => onShare(props.tab)}
                buttonRef={shareButtonRef}
                css={`
                  margin-left: ${action != null ? rpx(10) : 0}px;
                `}
              />
            )}
          </BottomAction>
        )}
      </>
    );
  },
);

const Refresher = memo(
  forwardRef<RefreshControl, { progressViewOffset?: number }>(
    ({ progressViewOffset }, ref) => {
      const { isRefreshing, refresh } = useReportContext();
      return (
        <RefreshControl
          ref={ref}
          refreshing={isRefreshing ?? false}
          onRefresh={refresh}
          progressViewOffset={progressViewOffset}
        />
      );
    },
  ),
);
